<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const connectors = ref([]);
const connectorId = ref('');
const busy = ref(false);
const error = ref('');
const result = ref(null);
const document = ref('');
const sex = ref('');
const enterpriseId = ref('');
const unitId = ref('');
const brokerId = ref('');
const billId = ref('');
const installmentId = ref('');
const tool = ref('sienge_enterprises');
const offset = ref(0);
const confirmRef = ref(null);
const selected = computed(() =>
  connectors.value.find(c => c.id === Number(connectorId.value))
);
const canWrite = computed(
  () => selected.value?.allow_writes && selected.value?.can_write
);
const rows = computed(
  () =>
    result.value?.results ||
    [result.value?.customer || result.value?.unit].filter(Boolean)
);
const tools = [
  'sienge_enterprises',
  'sienge_units',
  'sienge_customer',
  'sienge_contracts',
  'sienge_bills',
];
const more = computed(
  () =>
    offset.value + rows.value.length < (result.value?.pagination?.count || 0)
);

const load = async () => {
  try {
    const { data } = await ContactAPI.getSienge(props.contactId);
    connectors.value = data.connectors;
    if (!connectorId.value && data.connectors.length === 1)
      connectorId.value = data.connectors[0].id;
  } catch {
    error.value = t('SIENGE.ERROR');
  }
};
onMounted(load);

const request = async (action, payload = {}) => {
  busy.value = true;
  error.value = '';
  result.value = null;
  try {
    const { data } = await ContactAPI.siengeAction(props.contactId, action, {
      connector_id: connectorId.value,
      ...payload,
    });
    if (!data.success) throw new Error(data.error || t('SIENGE.ERROR'));
    result.value = data;
    return true;
  } catch (e) {
    error.value = e.response?.data?.error || e.message || t('SIENGE.ERROR');
    return false;
  } finally {
    busy.value = false;
  }
};

const query = async (nextTool = tool.value, params = {}, nextOffset = 0) => {
  if (params.installment_id) installmentId.value = params.installment_id;
  tool.value = nextTool;
  offset.value = nextOffset;
  await request('query', {
    tool: nextTool,
    enterprise_id: enterpriseId.value,
    unit_id: unitId.value,
    bill_id: billId.value,
    installment_id: installmentId.value,
    offset: nextOffset,
    ...params,
  });
};

const link = async () => {
  if (await request('link', { document: document.value })) {
    document.value = '';
    await load();
  }
};

const write = async action => {
  const confirmed = await confirmRef.value?.ask({
    title: t('SIENGE.CONFIRM_TITLE'),
    description: t(`SIENGE.CONFIRM_${action.toUpperCase()}`),
  });
  if (!confirmed) return;
  const success = await request(action, {
    confirmed: true,
    document: document.value,
    sex: sex.value,
    unit_id: unitId.value,
    broker_id: brokerId.value,
  });
  if (success) {
    document.value = '';
    await load();
  }
};

const showUnits = row => {
  enterpriseId.value = row.id;
  query('sienge_units');
};
const showInstallments = row => {
  billId.value = row.receivableBillId;
  query('sienge_installments');
};
const reset = () => {
  result.value = null;
  error.value = '';
  document.value = '';
  enterpriseId.value = '';
  unitId.value = '';
  billId.value = '';
  installmentId.value = '';
  tool.value = 'sienge_enterprises';
  offset.value = 0;
};
</script>

<template>
  <section
    v-if="connectors.length || error"
    class="m-4 space-y-4 rounded-xl border border-n-weak bg-n-solid-2 p-4"
  >
    <h3 class="font-semibold text-n-slate-12">{{ t('SIENGE.TITLE') }}</h3>
    <p class="text-xs text-n-slate-11">{{ t('SIENGE.CONTACT_HELP') }}</p>
    <label class="block text-sm text-n-slate-12">
      {{ t('TECIMOB.CONNECTION') }}
      <select
        v-model="connectorId"
        :disabled="busy"
        class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
        @change="reset"
      >
        <option value="">{{ t('TECIMOB.NONE') }}</option>
        <option
          v-for="connector in connectors"
          :key="connector.id"
          :value="connector.id"
        >
          {{ connector.name }}
        </option>
      </select>
    </label>
    <template v-if="selected">
      <form
        v-if="!selected.linked"
        class="flex flex-wrap items-end gap-2"
        @submit.prevent="link"
      >
        <label class="flex-1 text-sm text-n-slate-12">
          {{ t('SIENGE.DOCUMENT') }}
          <input
            v-model="document"
            :disabled="busy"
            autocomplete="off"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
          />
        </label>
        <Button
          :label="t('SIENGE.LINK')"
          :disabled="!document || busy"
          @click="link"
        />
      </form>
      <div class="flex flex-wrap items-end gap-2">
        <label class="flex-1 text-sm text-n-slate-12">
          {{ t('SIENGE.CONSULT') }}
          <select
            v-model="tool"
            :disabled="busy"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
            @change="result = null"
          >
            <option v-for="option in tools" :key="option" :value="option">
              {{ t(`SIENGE.TOOLS.${option}`) }}
            </option>
            <option
              v-if="tool === 'sienge_installments'"
              value="sienge_installments"
            >
              {{ t('SIENGE.TOOLS.sienge_installments') }}
            </option>
            <option
              v-if="tool === 'sienge_payment_slip'"
              value="sienge_payment_slip"
            >
              {{ t('SIENGE.TOOLS.sienge_payment_slip') }}
            </option>
            <option v-if="tool === 'sienge_unit'" value="sienge_unit">
              {{ t('SIENGE.TOOLS.sienge_unit') }}
            </option>
          </select>
        </label>
        <label v-if="tool === 'sienge_units'" class="text-sm text-n-slate-12">
          {{ t('SIENGE.ENTERPRISE_ID') }}
          <input
            v-model="enterpriseId"
            type="number"
            min="1"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
          />
        </label>
        <Button
          :label="t('SIENGE.LOAD')"
          :is-loading="busy"
          :disabled="busy"
          @click="query()"
        />
      </div>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <p
        v-if="result?.success && !rows.length && !result.url"
        class="text-sm text-n-slate-11"
      >
        {{
          result.data || result.duplicate
            ? t('SIENGE.COMPLETED')
            : t('SIENGE.EMPTY')
        }}
      </p>
      <a
        v-if="result?.url"
        :href="result.url"
        target="_blank"
        rel="noopener noreferrer"
        class="text-sm text-n-blue-11"
      >
        {{ t('SIENGE.OPEN_SLIP') }}
      </a>
      <p
        v-if="result?.digitable_number"
        class="break-all text-sm text-n-slate-12"
      >
        {{ result.digitable_number }}
      </p>
      <article
        v-for="(row, index) in rows"
        :key="row.id || index"
        class="space-y-3 rounded-lg border border-n-weak p-3"
      >
        <dl class="grid grid-cols-2 gap-2 text-sm">
          <template v-for="(value, field) in row" :key="field">
            <template v-if="value !== null && typeof value !== 'object'">
              <dt class="text-n-slate-11">{{ t(`SIENGE.FIELDS.${field}`) }}</dt>
              <dd class="break-words text-n-slate-12">
                {{
                  typeof value === 'boolean'
                    ? t(value ? 'SIENGE.YES' : 'SIENGE.NO')
                    : value
                }}
              </dd>
            </template>
          </template>
        </dl>
        <Button
          v-if="tool === 'sienge_enterprises'"
          :label="t('SIENGE.TOOLS.sienge_units')"
          :disabled="busy"
          @click="showUnits(row)"
        />
        <Button
          v-if="tool === 'sienge_units'"
          :label="t('SIENGE.TOOLS.sienge_unit')"
          :disabled="busy"
          @click="
            unitId = row.id;
            query('sienge_unit', { unit_id: row.id });
          "
        />
        <Button
          v-if="tool === 'sienge_bills'"
          :label="t('SIENGE.TOOLS.sienge_installments')"
          :disabled="busy"
          @click="showInstallments(row)"
        />
        <Button
          v-if="tool === 'sienge_installments' && row.generatedTicket"
          :label="t('SIENGE.OPEN_SLIP')"
          :disabled="busy"
          @click="
            query('sienge_payment_slip', { installment_id: row.installmentId })
          "
        />
      </article>
      <div v-if="more || offset" class="flex gap-2">
        <Button
          :label="t('SIENGE.PREVIOUS')"
          :disabled="busy || !offset"
          @click="query(tool, {}, Math.max(0, offset - 20))"
        />
        <Button
          :label="t('SIENGE.NEXT')"
          :disabled="busy || !more"
          @click="query(tool, {}, offset + 20)"
        />
      </div>
      <details v-if="canWrite" class="space-y-3 text-sm text-n-slate-12">
        <summary class="cursor-pointer">{{ t('SIENGE.ACTIONS') }}</summary>
        <p class="text-xs text-n-slate-11">{{ t('SIENGE.WRITE_HELP') }}</p>
        <div v-if="!selected.linked" class="flex flex-wrap gap-2">
          <select
            v-model="sex"
            :aria-label="t('SIENGE.SEX')"
            class="rounded-lg border border-n-weak bg-n-solid-1 p-2"
          >
            <option value="">{{ t('SIENGE.SEX') }}</option>
            <option value="F">{{ t('SIENGE.FEMALE') }}</option>
            <option value="M">{{ t('SIENGE.MALE') }}</option>
          </select>
          <Button
            :label="t('SIENGE.CREATE_CUSTOMER')"
            :disabled="!document || busy"
            @click="write('customer')"
          />
        </div>
        <div v-else class="flex flex-wrap gap-2">
          <label>
            {{ t('SIENGE.UNIT_ID') }}
            <input
              v-model="unitId"
              type="number"
              min="1"
              class="block w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
            />
          </label>
          <label>
            {{ t('SIENGE.BROKER_ID') }}
            <input
              v-model="brokerId"
              type="number"
              min="1"
              class="block w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
            />
          </label>
          <Button
            :label="t('SIENGE.RESERVE')"
            :disabled="!unitId || !brokerId || busy"
            @click="write('reservation')"
          />
        </div>
      </details>
    </template>
    <ConfirmDialog ref="confirmRef" />
  </section>
</template>
